import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import KamHome from './pages/kam/KamHome';
import RaiseTicket from './pages/kam/RaiseTicket';
import MyTickets from './pages/kam/MyTickets';
import TicketDetail from './pages/kam/TicketDetail';
import SupportDashboard from './pages/support/SupportDashboard';
import AllTickets from './pages/support/AllTickets';
import ResolveTicket from './pages/support/ResolveTicket';
import AdminDashboard from './pages/admin/AdminDashboard';
import DashHome from './pages/admin/dash/DashHome';
import ECMSPage from './pages/admin/dash/ECMSPage';
import IFleetPage from './pages/admin/dash/IFleetPage';
import AnalyzePage from './pages/admin/dash/AnalyzePage';
import ByEnterprise from './pages/admin/ByEnterprise';
import ByKam from './pages/admin/ByKam';
import ByAgent from './pages/admin/ByAgent';
import AllTicketsAdmin from './pages/admin/AllTicketsAdmin';
import AdminTicketDetail from './pages/admin/AdminTicketDetail';
import ManageSpecializations from './pages/admin/ManageSpecializations';
import ManageEnterprises from './pages/admin/ManageEnterprises';
import InvoiceDashboard from './pages/admin/InvoiceDashboard';
import InvoiceJobHistory from './pages/admin/InvoiceJobHistory';
import InvoiceJobResult from './pages/admin/InvoiceJobResult';
import InvoiceDetail from './pages/admin/InvoiceDetail';
import ZeerowBillingConfig from './pages/admin/ZeerowBillingConfig';
import ZeerowInvoice from './pages/admin/ZeerowInvoice';
import RoleManagement from './pages/admin/RoleManagement';
import UserManagement from './pages/admin/UserManagement';

// Returns the best landing page based on permissions.
// Empty permissions = super admin → admin dashboard.
function getHomePath(permissions) {
  if (!permissions || permissions.length === 0) return '/admin/dash/home';
  const checks = [
    ['DASH_VIEW',              '/admin/dash/home'],
    ['TICKET_MY_VIEW',         '/kam/home'],
    ['SUPPORT_VIEW',           '/support/dashboard'],
    ['TICKET_VIEW',            '/admin/tickets'],
    ['ECMS_ENTERPRISES_VIEW',  '/admin/ecms/enterprises'],
    ['ECMS_BILLING_VIEW',      '/admin/ecms/invoices'],
    ['IFLEET_ENTERPRISES_VIEW','/admin/ifleet/enterprises'],
    ['IFLEET_BILLING_VIEW',    '/admin/ifleet/invoices'],
    ['ZEEROW_VIEW',            '/admin/zeerow/config'],
    ['ROLE_MANAGE',            '/admin/roles'],
    ['USER_MANAGE',            '/admin/users'],
  ];
  for (const [perm, path] of checks) {
    if (permissions.includes(perm)) return path;
  }
  return '/admin/dash/home';
}

function PrivateRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={
        user ? <Navigate to={getHomePath(user.permissions)} replace /> : <LoginPage />
      } />
      <Route path="/kam/home" element={<PrivateRoute><KamHome /></PrivateRoute>} />
      <Route path="/kam/raise" element={<PrivateRoute><RaiseTicket /></PrivateRoute>} />
      <Route path="/kam/tickets" element={<PrivateRoute><MyTickets /></PrivateRoute>} />
      <Route path="/kam/tickets/:id" element={<PrivateRoute><TicketDetail /></PrivateRoute>} />
      <Route path="/support/dashboard" element={<PrivateRoute><SupportDashboard /></PrivateRoute>} />
      <Route path="/support/tickets" element={<PrivateRoute><AllTickets /></PrivateRoute>} />
      <Route path="/support/tickets/:id" element={<PrivateRoute><ResolveTicket /></PrivateRoute>} />
      <Route path="/admin/dashboard" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
      <Route path="/admin/dash/home"    element={<PrivateRoute><DashHome /></PrivateRoute>} />
      <Route path="/admin/dash/analyze" element={<PrivateRoute><AnalyzePage /></PrivateRoute>} />
      {/* Redirect old standalone enterprise detail pages to ECMS/iFleet enterprise lists */}
      <Route path="/admin/dash/ecms"    element={<Navigate to="/admin/ecms/enterprises" replace />} />
      <Route path="/admin/dash/ifleet"  element={<Navigate to="/admin/ifleet/enterprises" replace />} />
      <Route path="/admin/enterprise" element={<PrivateRoute><ByEnterprise /></PrivateRoute>} />
      <Route path="/admin/kam" element={<PrivateRoute><ByKam /></PrivateRoute>} />
      <Route path="/admin/agent" element={<PrivateRoute><ByAgent /></PrivateRoute>} />
      <Route path="/admin/tickets" element={<PrivateRoute><AllTicketsAdmin /></PrivateRoute>} />
      <Route path="/admin/tickets/:id" element={<PrivateRoute><AdminTicketDetail /></PrivateRoute>} />
      <Route path="/admin/specializations" element={<PrivateRoute><ManageSpecializations /></PrivateRoute>} />

      {/* ECMS routes */}
      <Route path="/admin/ecms/enterprises"          element={<PrivateRoute><ManageEnterprises source="ECMS" /></PrivateRoute>} />
      <Route path="/admin/ecms/invoices"             element={<PrivateRoute><InvoiceDashboard source="ECMS" /></PrivateRoute>} />
      <Route path="/admin/ecms/invoices/jobs"        element={<PrivateRoute><InvoiceJobHistory source="ECMS" /></PrivateRoute>} />
      <Route path="/admin/ecms/invoices/jobs/:jobId" element={<PrivateRoute><InvoiceJobResult source="ECMS" /></PrivateRoute>} />

      {/* iFleet routes */}
      <Route path="/admin/ifleet/enterprises"          element={<PrivateRoute><ManageEnterprises source="IFLEET" /></PrivateRoute>} />
      <Route path="/admin/ifleet/invoices"             element={<PrivateRoute><InvoiceDashboard source="IFLEET" /></PrivateRoute>} />
      <Route path="/admin/ifleet/invoices/jobs"        element={<PrivateRoute><InvoiceJobHistory source="IFLEET" /></PrivateRoute>} />
      <Route path="/admin/ifleet/invoices/jobs/:jobId" element={<PrivateRoute><InvoiceJobResult source="IFLEET" /></PrivateRoute>} />

      {/* Redirect old routes to ECMS */}
      <Route path="/admin/enterprises"        element={<Navigate to="/admin/ecms/enterprises" replace />} />
      <Route path="/admin/invoices"           element={<Navigate to="/admin/ecms/invoices" replace />} />
      <Route path="/admin/invoices/jobs"      element={<Navigate to="/admin/ecms/invoices/jobs" replace />} />

      <Route path="/admin/invoices/:invoiceId" element={<PrivateRoute><InvoiceDetail /></PrivateRoute>} />
      <Route path="/admin/zeerow/config" element={<PrivateRoute><ZeerowBillingConfig /></PrivateRoute>} />
      <Route path="/admin/zeerow/invoices" element={<PrivateRoute><ZeerowInvoice /></PrivateRoute>} />
      <Route path="/admin/roles" element={<PrivateRoute><RoleManagement /></PrivateRoute>} />
      <Route path="/admin/users" element={<PrivateRoute><UserManagement /></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Router>
          <AppRoutes />
        </Router>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
